import {
  DashboardDataResponse,
  DashboardFilterOptionsResponse,
  DashboardSelectionContext,
  DashboardSummary,
  DashboardWidgetDrilldownRequest,
  DashboardWidgetDrilldownResponse,
  DashboardWidgetDrilldownRow,
  DashboardWidgetResult
} from '../models/Dashboards/dashboard.models';
import { CHART_SERIES_COLOURS } from '../constants/chart-palette';

export const BLUEBIRD_DEMO_DASHBOARD_ID = 9001;

const blue = '#004ea8';
const brightBlue = '#1b8afd';
const navy = '#141b4d';
const paleBlue = '#90a5db';
const grey = '#d9d6ca';

const selection = (
  SelectionKey: string,
  RowType: DashboardSelectionContext['RowType'],
  extra: Partial<DashboardSelectionContext> = {}
): DashboardSelectionContext => ({ SelectionKey, RowType, ...extra });

export const bluebirdDemoDashboard: DashboardSummary = {
  DashboardID: BLUEBIRD_DEMO_DASHBOARD_ID,
  Name: 'Bluebird Care Quality Overview',
  IsDefault: true,
  IsSharedTemplate: true,
  IsLocked: true,
  Capabilities: {
    CanEditDashboard: false,
    CanCopyDashboard: false,
    CanDeleteDashboard: false,
    CanManageWidgets: false,
    CanSaveLayout: false
  }
};

export const bluebirdDemoFilters: DashboardFilterOptionsResponse = {
  DashboardID: BLUEBIRD_DEMO_DASHBOARD_ID,
  Offices: [
    { ID: 901, Label: 'Bluebird Care Central Office' },
    { ID: 902, Label: 'Bluebird Care Riverside' },
    { ID: 903, Label: 'Bluebird Care Westfield' }
  ],
  AuditTypes: [{ ID: 10, Label: 'Quality and compliance' }],
  Audits: [
    { ID: 2895, Label: 'Monthly Quality Audit' },
    { ID: 2896, Label: 'Quarterly Compliance Audit' },
    { ID: 2897, Label: 'Six-monthly Governance Audit' },
    { ID: 2898, Label: 'Annual Quality Review' }
  ],
  Frequencies: [
    { ID: 1, Label: 'Monthly' },
    { ID: 2, Label: 'Quarterly' },
    { ID: 3, Label: 'Six-monthly' },
    { ID: 4, Label: 'Annual' }
  ],
  SelectedOfficeID: 901,
  ShowOfficeSelector: true,
  OfficeSelectionRequired: false,
  QualityManagers: [
    { ID: 1001, Label: 'Temporary RQAL 1', IsActive: true, FranchiseCount: 2, RelatedOptionIDs: [2001, 2002] },
    { ID: 1002, Label: 'Temporary RQAL 2', IsActive: true, FranchiseCount: 1, RelatedOptionIDs: [2002] },
    { ID: 1099, Label: 'Temporary former RQAL', IsActive: false, FranchiseCount: 0, RelatedOptionIDs: [] }
  ],
  BusinessDevelopmentManagers: [
    { ID: 2001, Label: 'Temporary BDM 1', IsActive: true, FranchiseCount: 1, RelatedOptionIDs: [1001] },
    { ID: 2002, Label: 'Temporary BDM 2', IsActive: true, FranchiseCount: 2, RelatedOptionIDs: [1001, 1002] },
    { ID: 2099, Label: 'Temporary former BDM', IsActive: false, FranchiseCount: 0, RelatedOptionIDs: [] }
  ],
  ShowQualityManagerSelector: true,
  ShowBusinessDevelopmentManagerSelector: true,
  IsError: false
};

export const bluebirdDemoWidgets: DashboardWidgetResult[] = [
  {
    DashboardWidgetID: 9101,
    MetricDefinitionKey: 'bbc-mandatory-audit-progress',
    Title: 'Mandatory audit progress',
    Subtitle: 'Current reporting requirements completed',
    VisualType: 'radialProgress',
    Size: 'medium',
    DisplayOrder: 1,
    Value: 93,
    ValueSuffix: '%',
    Numerator: 168,
    Denominator: 180,
    Status: 'ok',
    Icon: 'task_alt',
    ChartData: {
      labels: ['Completed', 'Outstanding'],
      datasets: [{
        label: 'Requirements',
        data: [168, 12],
        backgroundColor: [blue, grey],
        borderColor: '#ffffff',
        borderWidth: 2,
        selections: [
          selection('completed', 'auditRequirement'),
          selection('incomplete', 'auditRequirement')
        ]
      }]
    },
    FooterText: '168 of 180 requirements complete'
  },
  {
    DashboardWidgetID: 9102,
    MetricDefinitionKey: 'bbc-overall-quality-comparison',
    Title: 'Overall quality',
    Subtitle: 'Latest completed audits compared with prior results',
    VisualType: 'metricGroup',
    Size: 'medium',
    DisplayOrder: 2,
    Status: 'ok',
    Icon: 'insights',
    MetricValues: [
      { Label: 'Current score', Value: 86.8, Suffix: '%', Status: 'ok', TrendText: 'On target', Selection: selection('current', 'pillar') },
      { Label: 'Previous score', Value: 82.5, Suffix: '%', Status: 'warning', TrendText: 'Previous cycle', Selection: selection('previous', 'pillar') },
      { Label: 'Movement', Value: '+4.3', Suffix: ' pts', Status: 'ok', TrendText: 'Improving', AggregateOnly: true }
    ],
    FooterText: 'Six quality pillars represented'
  },
  {
    DashboardWidgetID: 9103,
    MetricDefinitionKey: 'bbc-frequency-score-trend',
    Title: 'Quality score trend',
    Subtitle: 'Performance across the last four closed periods',
    VisualType: 'line',
    Size: 'large',
    DisplayOrder: 5,
    Status: 'ok',
    Icon: 'show_chart',
    ChartData: {
      labels: ['Period 1', 'Period 2', 'Period 3', 'Latest'],
      datasets: [
        { label: 'Monthly', data: [79, 82, 84, 88], pointLabels: ['May', 'Jun', 'Jul', 'Aug'], borderColor: brightBlue, backgroundColor: 'rgba(27, 138, 253, .12)', borderWidth: 3, tension: .3, fill: false },
        { label: 'Quarterly', data: [76, 80, 83, 86], pointLabels: ['Q3 2025', 'Q4 2025', 'Q1 2026', 'Q2 2026'], borderColor: navy, backgroundColor: navy, borderWidth: 2, tension: .3, fill: false },
        { label: 'Six-monthly', data: [78, 81, 84, 87], pointLabels: ['H1 2025', 'H2 2025', 'H1 2026', 'H2 2026'], borderColor: paleBlue, backgroundColor: paleBlue, borderWidth: 2, tension: .3, fill: false },
        { label: 'Annual', data: [74, 79, 83, 86], pointLabels: ['2023', '2024', '2025', '2026'], borderColor: CHART_SERIES_COLOURS.bluebirdAnnual, backgroundColor: CHART_SERIES_COLOURS.bluebirdAnnual, borderWidth: 2, tension: .3, fill: false }
      ]
    },
    FooterText: 'All four audit frequencies are trending upward'
  },
  {
    DashboardWidgetID: 9104,
    MetricDefinitionKey: 'bbc-mandatory-compliance-rate',
    Title: 'Mandatory compliance rate',
    Subtitle: 'Completed requirements across the current cycle',
    VisualType: 'percentage',
    Size: 'small',
    DisplayOrder: 3,
    Value: 93.3,
    ValueSuffix: '%',
    TrendText: '+3.8 pts vs previous cycle',
    TrendDirection: 'up',
    Status: 'ok',
    Icon: 'verified',
    Selection: selection('completed', 'auditRequirement'),
    FooterText: 'Target 90%'
  },
  {
    DashboardWidgetID: 9105,
    MetricDefinitionKey: 'bbc-lowest-pillar',
    Title: 'Pillar requiring attention',
    Subtitle: 'Lowest current quality pillar',
    VisualType: 'percentage',
    Size: 'small',
    DisplayOrder: 4,
    Value: 78.4,
    ValueSuffix: '%',
    TrendText: 'People & Culture · +2.1 pts',
    TrendDirection: 'up',
    Status: 'warning',
    Icon: 'groups',
    Selection: selection('pillar', 'pillar', { PillarID: 4 }),
    FooterText: 'Improving, but below the 85% target'
  },
  {
    DashboardWidgetID: 9106,
    MetricDefinitionKey: 'bbc-overdue-action-trend',
    Title: 'Overdue action trend',
    Subtitle: 'Current position against the 30-day average',
    VisualType: 'metricGroup',
    Size: 'medium',
    DisplayOrder: 6,
    Status: 'warning',
    Icon: 'trending_down',
    MetricValues: [
      { Label: 'Overdue now', Value: 9, Status: 'warning', TrendText: '4 fewer than last month', Selection: selection('overdue', 'action') },
      { Label: '30-day average', Value: 12.4, Status: 'neutral', TrendText: 'Daily open average', AggregateOnly: true }
    ],
    FooterText: 'Current overdue actions are below the recent average'
  },
  {
    DashboardWidgetID: 9107,
    MetricDefinitionKey: 'bbc-open-action-summary',
    Title: 'Action follow-up',
    Subtitle: 'Current and recently completed actions',
    VisualType: 'table',
    Size: 'medium',
    DisplayOrder: 7,
    Status: 'warning',
    Icon: 'assignment',
    TableRows: [
      { Label: 'Open actions', Value: '42', Detail: 'Across all active audits', Status: 'warning', Selection: selection('open', 'action') },
      { Label: 'Overdue', Value: '9', Detail: 'Immediate owner follow-up required', Status: 'danger', Selection: selection('overdue', 'action') },
      { Label: 'Due this month', Value: '17', Detail: 'Including actions already overdue', Status: 'warning', Selection: selection('dueThisMonth', 'action') },
      { Label: 'Completed this month', Value: '28', Detail: 'Resolved during August', Status: 'ok', Selection: selection('completedThisMonth', 'action') }
    ],
    FooterText: '67% of this month’s action workload completed'
  },
  {
    DashboardWidgetID: 9108,
    MetricDefinitionKey: 'bbc-pillar-performance',
    Title: 'Quality pillar performance',
    Subtitle: 'Current score, movement and audit coverage',
    VisualType: 'table',
    Size: 'large',
    DisplayOrder: 9,
    Status: 'ok',
    Icon: 'leaderboard',
    TableRows: [
      { Rank: 1, Label: 'Safe & Legal', Value: '92.1%', PreviousValue: '88.4%', Movement: '+3.7', Category: 'Green', Coverage: '28/30 audits', Detail: '', Status: 'ok', Selection: selection('pillar', 'pillar', { PillarID: 2 }) },
      { Rank: 2, Label: 'Regulatory Care', Value: '89.6%', PreviousValue: '85.1%', Movement: '+4.5', Category: 'Green', Coverage: '34/36 audits', Detail: '', Status: 'ok', Selection: selection('pillar', 'pillar', { PillarID: 5 }) },
      { Rank: 3, Label: 'Operational Excellence', Value: '87.3%', PreviousValue: '83.8%', Movement: '+3.5', Category: 'Green', Coverage: '31/34 audits', Detail: '', Status: 'ok', Selection: selection('pillar', 'pillar', { PillarID: 1 }) },
      { Rank: 4, Label: 'Finance', Value: '84.5%', PreviousValue: '80.2%', Movement: '+4.3', Category: 'Amber', Coverage: '16/18 audits', Detail: '', Status: 'warning', Selection: selection('pillar', 'pillar', { PillarID: 6 }) },
      { Rank: 5, Label: 'Branding & Marketing', Value: '81.7%', PreviousValue: '79.4%', Movement: '+2.3', Category: 'Amber', Coverage: '19/22 audits', Detail: '', Status: 'warning', Selection: selection('pillar', 'pillar', { PillarID: 3 }) },
      { Rank: 6, Label: 'People & Culture', Value: '78.4%', PreviousValue: '76.3%', Movement: '+2.1', Category: 'Amber', Coverage: '24/28 audits', Detail: '', Status: 'warning', Selection: selection('pillar', 'pillar', { PillarID: 4 }) }
    ],
    FooterText: 'Scores are based on the latest completed audit at each office'
  },
  {
    DashboardWidgetID: 9109,
    MetricDefinitionKey: 'bbc-mandatory-compliance-summary',
    Title: 'Mandatory audit status',
    Subtitle: 'Completion by audit programme',
    VisualType: 'table',
    Size: 'large',
    DisplayOrder: 8,
    Status: 'warning',
    Icon: 'fact_check',
    TableRows: [
      { Label: 'Monthly Quality Audit', Value: '96%', Detail: '86 of 90 complete', Frequency: 'Monthly', Period: '01–31 Aug', CompletionDate: 'Latest 9 Aug', Status: 'ok', Selection: selection('audit', 'auditRequirement', { AuditID: 2895 }) },
      { Label: 'Quarterly Compliance Audit', Value: '92%', Detail: '44 of 48 complete', Frequency: 'Quarterly', Period: 'Jul–Sep', CompletionDate: 'Latest 8 Aug', Status: 'ok', Selection: selection('audit', 'auditRequirement', { AuditID: 2896 }) },
      { Label: 'Governance Audit', Value: '88%', Detail: '21 of 24 complete', Frequency: 'Six-monthly', Period: 'Jul–Dec', CompletionDate: 'Latest 6 Aug', Status: 'warning', Selection: selection('audit', 'auditRequirement', { AuditID: 2897 }) },
      { Label: 'Annual Quality Review', Value: '94%', Detail: '17 of 18 complete', Frequency: 'Annual', Period: '2026', CompletionDate: 'Latest 2 Aug', Status: 'ok', Selection: selection('audit', 'auditRequirement', { AuditID: 2898 }) }
    ],
    FooterText: '12 requirements remain outstanding across the network'
  },
  {
    DashboardWidgetID: 9110,
    MetricDefinitionKey: 'bbc-lowest-pillars',
    Title: 'Priority improvement areas',
    Subtitle: 'Lowest-scoring pillars across the network',
    VisualType: 'table',
    Size: 'medium',
    DisplayOrder: 10,
    Status: 'warning',
    Icon: 'priority_high',
    TableRows: [
      { Rank: 1, Label: 'People & Culture', Value: '78.4%', PreviousValue: '76.3%', Movement: '+2.1', Category: 'Amber', Coverage: '24/28', Detail: '', Status: 'warning', Selection: selection('pillar', 'pillar', { PillarID: 4 }) },
      { Rank: 2, Label: 'Branding & Marketing', Value: '81.7%', PreviousValue: '79.4%', Movement: '+2.3', Category: 'Amber', Coverage: '19/22', Detail: '', Status: 'warning', Selection: selection('pillar', 'pillar', { PillarID: 3 }) },
      { Rank: 3, Label: 'Finance', Value: '84.5%', PreviousValue: '80.2%', Movement: '+4.3', Category: 'Amber', Coverage: '16/18', Detail: '', Status: 'warning', Selection: selection('pillar', 'pillar', { PillarID: 6 }) }
    ],
    FooterText: 'All three areas improved from their previous results'
  },
  {
    DashboardWidgetID: 9111,
    MetricDefinitionKey: 'bbc-repeated-findings',
    Title: 'Repeated findings',
    Subtitle: 'Most frequent non-compliant findings this year',
    VisualType: 'table',
    Size: 'large',
    DisplayOrder: 11,
    Status: 'warning',
    Icon: 'rule',
    TableRows: [
      { Label: 'Medication competency records require review', Value: '12', Detail: 'Safe & Legal', Coverage: '7 offices · 9 audits', AffectedAuditCount: 9, Status: 'danger', Selection: selection('questionFinding', 'question', { PillarID: 2, QuestionID: 41001 }) },
      { Label: 'Care plan review date not evidenced', Value: '9', Detail: 'Regulatory Care', Coverage: '6 offices · 8 audits', AffectedAuditCount: 8, Status: 'warning', Selection: selection('questionFinding', 'question', { PillarID: 5, QuestionID: 41002 }) },
      { Label: 'Supervision record incomplete', Value: '7', Detail: 'People & Culture', Coverage: '5 offices · 6 audits', AffectedAuditCount: 6, Status: 'warning', Selection: selection('questionFinding', 'question', { PillarID: 4, QuestionID: 41003 }) },
      { Label: 'Business continuity action overdue', Value: '5', Detail: 'Operational Excellence', Coverage: '4 offices · 5 audits', AffectedAuditCount: 5, Status: 'warning', Selection: selection('questionFinding', 'question', { PillarID: 1, QuestionID: 41004 }) }
    ],
    FooterText: 'Select a finding to view the contributing audit records'
  }
];

export function buildBluebirdDemoDashboardData(): DashboardDataResponse {
  return {
    DashboardID: BLUEBIRD_DEMO_DASHBOARD_ID,
    Widgets: bluebirdDemoWidgets
      .map(widget => ({ ...widget }))
      .sort((left, right) => (left.DisplayOrder ?? 0) - (right.DisplayOrder ?? 0)),
    LastUpdatedDisplay: 'Today, 10:30',
    IsError: false
  };
}

const demoRows: DashboardWidgetDrilldownRow[] = [
  { RowID: 9201, RowType: 'auditResult', PrimaryText: 'Monthly Quality Audit', SecondaryText: 'Score 91% · 2 follow-up items', LocationName: 'Bluebird Care Central Office', DateDisplay: '09 Aug 2026', Owner: 'Quality Team', Status: 'ok', StatusText: 'Complete', ActionLabel: 'View result', LinkUrl: '/results' },
  { RowID: 9202, RowType: 'action', PrimaryText: 'Review medication competency record', SecondaryText: 'Raised from Safe & Legal audit', LocationName: 'Bluebird Care Riverside', DateDisplay: '08 Aug 2026', Owner: 'Registered Manager', Status: 'danger', StatusText: 'Overdue', ActionLabel: 'View action', LinkUrl: '/actionv2' },
  { RowID: 9203, RowType: 'question', PrimaryText: 'Care plan reviewed within required period?', SecondaryText: 'Repeated finding across six offices', LocationName: 'Bluebird Care Westfield', DateDisplay: '07 Aug 2026', Owner: 'Quality Lead', Status: 'warning', StatusText: 'Follow-up', ActionLabel: 'View result', LinkUrl: '/results' },
  { RowID: 9204, RowType: 'auditRequirement', PrimaryText: 'Quarterly Compliance Audit', SecondaryText: 'Current requirement completed', LocationName: 'Bluebird Care Central Office', DateDisplay: '06 Aug 2026', Owner: 'Operations Manager', Status: 'ok', StatusText: 'Complete', ActionLabel: 'View result', LinkUrl: '/results' },
  { RowID: 9205, RowType: 'pillar', PrimaryText: 'People & Culture', SecondaryText: 'Current 78.4% · previous 76.3%', LocationName: 'Bluebird Care Network', DateDisplay: '05 Aug 2026', Owner: 'People Team', Status: 'warning', StatusText: 'Improving', ActionLabel: 'View result', LinkUrl: '/results' },
  { RowID: 9206, RowType: 'action', PrimaryText: 'Complete staff supervision record', SecondaryText: 'Due within seven days', LocationName: 'Bluebird Care Riverside', DateDisplay: '04 Aug 2026', Owner: 'Branch Manager', Status: 'warning', StatusText: 'Due soon', ActionLabel: 'View action', LinkUrl: '/actionv2' }
];

const demoOverdueActionRows: DashboardWidgetDrilldownRow[] = [
  { RowID: 9301, RowType: 'action', PrimaryText: 'Review medication competency records', SecondaryText: 'Raised from the Safe & Legal audit', LocationName: 'Bluebird Care Riverside', DateDisplay: '28 Jul 2026', Owner: 'Registered Manager', Status: 'danger', StatusText: '13 days overdue', ActionLabel: 'View action', LinkUrl: '/actionv2' },
  { RowID: 9302, RowType: 'action', PrimaryText: 'Update care plan review evidence', SecondaryText: 'Evidence required for three service users', LocationName: 'Bluebird Care Central Office', DateDisplay: '30 Jul 2026', Owner: 'Quality Lead', Status: 'danger', StatusText: '11 days overdue', ActionLabel: 'View action', LinkUrl: '/actionv2' },
  { RowID: 9303, RowType: 'action', PrimaryText: 'Complete outstanding staff supervisions', SecondaryText: 'Two supervision records remain incomplete', LocationName: 'Bluebird Care Westfield', DateDisplay: '01 Aug 2026', Owner: 'Branch Manager', Status: 'danger', StatusText: '9 days overdue', ActionLabel: 'View action', LinkUrl: '/actionv2' },
  { RowID: 9304, RowType: 'action', PrimaryText: 'Confirm business continuity call tree', SecondaryText: 'Annual contact validation is incomplete', LocationName: 'Bluebird Care Northgate', DateDisplay: '03 Aug 2026', Owner: 'Operations Manager', Status: 'danger', StatusText: '7 days overdue', ActionLabel: 'View action', LinkUrl: '/actionv2' },
  { RowID: 9305, RowType: 'action', PrimaryText: 'Record infection-control spot check', SecondaryText: 'Follow-up evidence required from the latest audit', LocationName: 'Bluebird Care Meadow Park', DateDisplay: '05 Aug 2026', Owner: 'Clinical Lead', Status: 'danger', StatusText: '5 days overdue', ActionLabel: 'View action', LinkUrl: '/actionv2' },
  { RowID: 9306, RowType: 'action', PrimaryText: 'Upload recruitment file checks', SecondaryText: 'Missing right-to-work verification evidence', LocationName: 'Bluebird Care Lakeside', DateDisplay: '06 Aug 2026', Owner: 'People Manager', Status: 'danger', StatusText: '4 days overdue', ActionLabel: 'View action', LinkUrl: '/actionv2' },
  { RowID: 9307, RowType: 'action', PrimaryText: 'Close safeguarding learning action', SecondaryText: 'Team briefing completed; attendance record outstanding', LocationName: 'Bluebird Care Central Office', DateDisplay: '07 Aug 2026', Owner: 'Safeguarding Lead', Status: 'danger', StatusText: '3 days overdue', ActionLabel: 'View action', LinkUrl: '/actionv2' }
];

export function buildBluebirdDemoDrilldown(request: DashboardWidgetDrilldownRequest): DashboardWidgetDrilldownResponse {
  const widget = bluebirdDemoWidgets.find(item => item.DashboardWidgetID === request.DashboardWidgetID);
  const sourceRows = request.RowType === 'action' && request.SelectionKey === 'overdue'
    ? demoOverdueActionRows
    : demoRows;
  const rows = [...sourceRows].sort((left, right) => {
    const leftValue = request.SortField === 'DateDisplay'
      ? Date.parse(left.DateDisplay)
      : String(left[request.SortField] ?? '').toLocaleLowerCase();
    const rightValue = request.SortField === 'DateDisplay'
      ? Date.parse(right.DateDisplay)
      : String(right[request.SortField] ?? '').toLocaleLowerCase();
    const comparison = typeof leftValue === 'number' && typeof rightValue === 'number'
      ? leftValue - rightValue
      : String(leftValue).localeCompare(String(rightValue));
    return request.SortDirection === 'asc' ? comparison : -comparison;
  });
  const start = (request.PageNumber - 1) * request.PageSize;

  return {
    DashboardWidgetID: request.DashboardWidgetID,
    Title: request.RowType === 'action' && request.SelectionKey === 'overdue'
      ? 'Overdue actions requiring follow-up'
      : `${widget?.Title ?? 'Dashboard'} details`,
    TotalRows: rows.length,
    PageNumber: request.PageNumber,
    PageSize: request.PageSize,
    Rows: rows.slice(start, start + request.PageSize)
  };
}
